import React, { useState, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Upload, Image, Video, FileText, Music, Loader2, Bold, Italic, List, Link2, Type, Quote } from 'lucide-react';

const RichTextEditor = ({
  value,
  onChange,
  placeholder = "Start writing your blog post...",
  blogId = null
}) => {
  const [uploading, setUploading] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const textareaRef = useRef(null);

  // Handle file upload (placeholder - media upload not implemented)
  const handleFileUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    // TODO: Implement media upload functionality
    alert('Media upload functionality is not yet implemented. Please use markdown syntax for images: ![alt text](image-url)');
    return [];
  }, []);

  // Handle image insert from toolbar
  // Handle text formatting with markdown
  const insertText = useCallback((before, after = '') => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = value.substring(start, end);
      const newText = value.substring(0, start) + before + selectedText + after + value.substring(end);
      onChange(newText);

      // Restore cursor position
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + before.length, end + before.length);
      }, 0);
    }
  }, [value, onChange]);

  // Formatting functions
  const makeBold = () => insertText('**', '**');
  const makeItalic = () => insertText('*', '*');
  const makeHeading = () => insertText('## ');
  const makeList = () => insertText('- ');
  const makeLink = () => insertText('[', '](url)');
  const makeQuote = () => insertText('> ');

  // Handle image insert
  const handleImageInsert = useCallback(() => {
    const url = prompt('Enter image URL:');
    if (url) {
      insertText(`![Image](${url})`);
    }
  }, [insertText]);

  // Handle video insert
  const handleVideoInsert = useCallback(() => {
    const url = prompt('Enter video URL:');
    if (url) {
      insertText(`[Video](${url})`);
    }
  }, [insertText]);



  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileUpload,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'video/*': ['.mp4', '.webm', '.ogg'],
      'audio/*': ['.mp3', '.wav', '.ogg'],
      'application/pdf': ['.pdf'],
      'text/*': ['.txt', '.md']
    },
    multiple: true,
    maxSize: 10 * 1024 * 1024 // 10MB
  });



  // Get media type icon
  const getMediaIcon = (type) => {
    switch (type) {
      case 'image': return <Image className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      case 'audio': return <Music className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      {/* Formatting Toolbar */}
      <div className="border border-slate-200 rounded-lg p-2 bg-slate-50">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={makeBold}
            className="h-8 px-2"
          >
            <Bold className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={makeItalic}
            className="h-8 px-2"
          >
            <Italic className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={makeHeading}
            className="h-8 px-2"
          >
            <Type className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={makeList}
            className="h-8 px-2"
          >
            <List className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={makeQuote}
            className="h-8 px-2"
          >
            <Quote className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={makeLink}
            className="h-8 px-2"
          >
            <Link2 className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleImageInsert}
            className="h-8 px-2"
          >
            <Image className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Text Editor */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="min-h-[300px] border-0 resize-none focus:ring-0 focus:border-0"
          style={{ minHeight: '300px' }}
        />
      </div>

      {/* Media Upload Dropzone */}
      <Card className="border-dashed border-2 border-slate-300 hover:border-blue-400 transition-colors">
        <CardContent className="p-6">
          <div
            {...getRootProps()}
            className={`text-center cursor-pointer ${
              isDragActive ? 'bg-blue-50 border-blue-400' : ''
            }`}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center space-y-2">
              {uploading ? (
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              ) : (
                <Upload className="w-8 h-8 text-slate-400" />
              )}
              <div>
                <p className="text-sm font-medium text-slate-700">
                  {uploading ? 'Uploading...' : 'Drop files here or click to upload'}
                </p>
                <p className="text-xs text-slate-500">
                  Images, videos, audio, documents (max 10MB each)
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Media Library */}
      {mediaItems.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-slate-900">Uploaded Media</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowMediaLibrary(!showMediaLibrary)}
              >
                {showMediaLibrary ? 'Hide' : 'Show'} Library
              </Button>
            </div>
            
            {showMediaLibrary && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {mediaItems.map((media) => (
                  <div
                    key={media.id}
                    className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 cursor-pointer"
                    onClick={() => insertMediaIntoEditor(media)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {getMediaIcon(media.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {media.filename}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatFileSize(media.size)}
                        </p>
                        <Badge variant="secondary" className="mt-1">
                          {media.type}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Usage Instructions */}
      <div className="text-xs text-slate-500 space-y-1">
        <p>• Use the toolbar buttons to format text with markdown</p>
        <p>• **Bold text**, *italic text*, ## Headings, - Lists</p>
        <p>• Images: ![alt text](image-url) | Links: [text](url)</p>
        <p>• Drag and drop files into the upload area (placeholder)</p>
      </div>
    </div>
  );
};

export default RichTextEditor;
